import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { resolveImageUrl } from "@/utils/imageUrl";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, CheckCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ProductDialog({
  open, onOpenChange, editingProduct, productForm, setProductForm,
  handleProductInputChange, handleProductSubmit, saving,
  dropdownOptions, setEditingDropdown,
}) {
  const { token } = useAuth();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-heading">{editingProduct ? "Edit Product" : "Add New Product"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleProductSubmit} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input name="title" value={productForm.title} onChange={handleProductInputChange} placeholder="Mathematics Mock Exam..." required className="rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label>Slug *</Label>
              <Input name="slug" value={productForm.slug} onChange={handleProductInputChange} required className="rounded-xl" />
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Subject *</Label>
                <button type="button" onClick={() => setEditingDropdown("subjects")} className="text-xs text-accent hover:underline">Manage</button>
              </div>
              <Select value={productForm.subject} onValueChange={(v) => setProductForm(prev => ({ ...prev, subject: v }))}>
                <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{dropdownOptions.subjects.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Class Level *</Label>
                <button type="button" onClick={() => setEditingDropdown("class_levels")} className="text-xs text-accent hover:underline">Manage</button>
              </div>
              <Select value={productForm.class_level} onValueChange={(v) => setProductForm(prev => ({ ...prev, class_level: v }))}>
                <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{dropdownOptions.class_levels.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Price (GHS) *</Label>
              <Input name="price_ghs" type="number" step="0.01" value={productForm.price_ghs} onChange={handleProductInputChange} required className="rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label>Compare at Price</Label>
              <Input name="compare_at_price_ghs" type="number" step="0.01" value={productForm.compare_at_price_ghs} onChange={handleProductInputChange} className="rounded-xl" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Description *</Label>
            <Textarea name="description" value={productForm.description} onChange={handleProductInputChange} rows={3} required className="rounded-xl" />
          </div>
          <div className="space-y-2">
            <Label>Cover Image URL</Label>
            <div className="flex gap-3">
              <Input name="cover_image_url" value={productForm.cover_image_url} onChange={handleProductInputChange} placeholder="https://... or upload below" className="rounded-xl" data-testid="cover-image-url-input" />
              {productForm.cover_image_url && (
                <img src={resolveImageUrl(productForm.cover_image_url)} alt="Preview" className="w-12 h-12 rounded-lg object-cover border" />
              )}
            </div>
            <div className="flex gap-3 items-center">
              <input
                type="file"
                accept="image/*"
                className="text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-4 file:rounded-xl file:border-0 file:bg-primary/10 file:text-primary file:font-medium file:cursor-pointer"
                data-testid="cover-image-upload"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const fd = new FormData();
                  fd.append("file", file);
                  try {
                    const res = await axios.post(`${API}/admin/upload-image`, fd, {
                      headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
                    });
                    setProductForm(prev => ({ ...prev, cover_image_url: res.data.url }));
                    toast.success("Image uploaded");
                  } catch (err) {
                    toast.error("Upload failed");
                  }
                }}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Product File (PDF or ZIP)</Label>
            {productForm.file_path && (
              <div className="flex items-center gap-2 bg-green-50 text-green-700 p-3 rounded-xl text-sm">
                <CheckCircle className="w-4 h-4 shrink-0" />
                <span className="truncate">{productForm.file_path.split("/").pop()}</span>
                {productForm.file_size_mb && <span className="text-xs text-green-500 shrink-0">({productForm.file_size_mb} MB)</span>}
              </div>
            )}
            <div className="flex gap-3 items-center">
              <input
                type="file"
                accept=".pdf,.zip"
                className="text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-4 file:rounded-xl file:border-0 file:bg-accent/10 file:text-accent file:font-medium file:cursor-pointer"
                data-testid="product-file-upload"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const fd = new FormData();
                  fd.append("file", file);
                  toast.info(`Uploading ${file.name}...`);
                  try {
                    const res = await axios.post(`${API}/admin/upload-file`, fd, {
                      headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
                    });
                    setProductForm(prev => ({
                      ...prev,
                      file_path: res.data.file_key,
                      file_size_mb: (res.data.size / (1024 * 1024)).toFixed(2)
                    }));
                    toast.success(`${file.name} uploaded to storage`);
                  } catch (err) {
                    toast.error("File upload failed");
                  }
                }}
              />
            </div>
            <p className="text-xs text-gray-400">Upload a PDF file or a ZIP folder containing multiple PDFs. Each ZIP = one sellable product.</p>
          </div>
          {/* Pages, Questions, Duration */}
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Pages</Label>
              <Input name="page_count" type="number" value={productForm.page_count || ""} onChange={handleProductInputChange} placeholder="e.g. 12" className="rounded-xl" data-testid="page-count-input" />
            </div>
            <div className="space-y-2">
              <Label>Questions</Label>
              <Input name="question_count" type="number" value={productForm.question_count || ""} onChange={handleProductInputChange} placeholder="e.g. 47" className="rounded-xl" data-testid="question-count-input" />
            </div>
            <div className="space-y-2">
              <Label>Duration (Minutes)</Label>
              <Input name="duration_minutes" type="number" value={productForm.duration_minutes || ""} onChange={handleProductInputChange} placeholder="e.g. 75" className="rounded-xl" data-testid="duration-input" />
            </div>
          </div>
          {/* Features */}
          <div className="space-y-2">
            <Label>Features</Label>
            <p className="text-xs text-gray-400">List of features shown on the product detail page</p>
            <div className="space-y-2">
              {(productForm.features || []).map((feat, idx) => (
                <div key={idx} className="flex gap-2">
                  <Input
                    value={feat}
                    onChange={(e) => {
                      const updated = [...(productForm.features || [])];
                      updated[idx] = e.target.value;
                      setProductForm(prev => ({ ...prev, features: updated }));
                    }}
                    className="rounded-xl"
                    data-testid={`feature-input-${idx}`}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const updated = (productForm.features || []).filter((_, i) => i !== idx);
                      setProductForm(prev => ({ ...prev, features: updated }));
                    }}
                    className="rounded-xl text-red-500 hover:text-red-700 shrink-0"
                    data-testid={`remove-feature-${idx}`}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setProductForm(prev => ({ ...prev, features: [...(prev.features || []), ""] }))}
                className="rounded-xl"
                data-testid="add-feature-btn"
              >
                + Add Feature
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center gap-2">
              <Checkbox id="is_active" checked={productForm.is_active} onCheckedChange={(c) => setProductForm(prev => ({ ...prev, is_active: c }))} />
              <label htmlFor="is_active">Active</label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="is_featured" checked={productForm.is_featured} onCheckedChange={(c) => setProductForm(prev => ({ ...prev, is_featured: c }))} />
              <label htmlFor="is_featured">Featured</label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="is_bundle" checked={productForm.is_bundle} onCheckedChange={(c) => setProductForm(prev => ({ ...prev, is_bundle: c }))} />
              <label htmlFor="is_bundle">Standard Question Bank</label>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Recommend To (optional)</Label>
            <p className="text-xs text-gray-400">Override auto-suggest — explicitly show this product to selected roles</p>
            <div className="flex flex-wrap gap-3">
              {[
                { value: "student", label: "Students", color: "border-blue-400 bg-blue-50 text-blue-700" },
                { value: "teacher", label: "Teachers", color: "border-emerald-400 bg-emerald-50 text-emerald-700" },
                { value: "parent", label: "Parents", color: "border-purple-400 bg-purple-50 text-purple-700" },
                { value: "school_admin", label: "School Admins", color: "border-amber-400 bg-amber-50 text-amber-700" },
              ].map(({ value, label, color }) => {
                const isSelected = (productForm.target_roles || []).includes(value);
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => {
                      setProductForm(prev => ({
                        ...prev,
                        target_roles: isSelected
                          ? prev.target_roles.filter(r => r !== value)
                          : [...(prev.target_roles || []), value]
                      }));
                    }}
                    className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${
                      isSelected ? `${color} border-2 shadow-sm` : "border-gray-200 bg-white text-gray-500 hover:border-gray-300"
                    }`}
                    data-testid={`target-role-${value}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl">Cancel</Button>
            <Button type="submit" className="rounded-xl bg-primary" disabled={saving}>
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : editingProduct ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
